/**
 * NocturneShell — Preset 2 primary shell (Vault edition).
 *
 * Improvements vs prior version:
 *  - Brand reads from config/site (SITE_NAME) — falls back to "GameVault999".
 *    Never hardcoded "Nocturne" client-side.
 *  - Stronger active-nav indicator with smooth amber slide.
 *  - Mobile bottom nav: 5 thumb-friendly items (Home / Games / Deposit / Rewards / Profile)
 *    with the centre Deposit as a gold CTA pill.
 *  - Notification dot when unread inbox > 0.
 *  - Desktop top-bar gains a quick-action chip row (Deposit / Games / Rewards).
 *  - Balance chip uses tabular-nums and a count-up popover.
 */
import { useMemo, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Home as HomeIcon,
    Wallet as WalletIcon,
    Gamepad2,
    Trophy,
    Inbox as InboxIcon,
    MessageCircle,
    User as UserIcon,
    Menu as MenuIcon,
    X as CloseIcon,
    ArrowDownLeft,
    ArrowUpRight,
    ChevronRight,
    Bell,
} from "lucide-react";

import { useApp } from "@/context/AppContext";
import { hasCapability } from "@/config/capabilities";
import { SITE_NAME } from "@/config/site.juwa";
import VerifiedBadge from "@/components/VerifiedBadge";

const BRAND = (SITE_NAME && String(SITE_NAME).trim()) || "Juwa";

const NAV_ITEMS = [
    { to: "/home", label: "Home", icon: HomeIcon, capability: null },
    {
        to: "/wallet",
        label: "Wallet",
        icon: WalletIcon,
        capability: "client.wallet",
    },
    {
        to: "/games",
        label: "Games",
        icon: Gamepad2,
        capability: "client.games",
    },
    { to: "/achievements", label: "Rewards", icon: Trophy, capability: null },
    { to: "/inbox", label: "Inbox", icon: InboxIcon, capability: null },
    {
        to: "/messages",
        label: "Chat",
        icon: MessageCircle,
        capability: "client.chat",
    },
    { to: "/profile", label: "Profile", icon: UserIcon, capability: null },
];

// Mobile bottom nav: only 5 priorities. Deposit is the centre gold CTA.
const MOBILE_NAV_LEFT = [
    { to: "/home", label: "Home", icon: HomeIcon },
    { to: "/games", label: "Games", icon: Gamepad2 },
];
const MOBILE_NAV_RIGHT = [
    { to: "/achievements", label: "Rewards", icon: Trophy },
    { to: "/profile", label: "Profile", icon: UserIcon },
];

function formatAmount(n) {
    if (typeof n !== "number" || Number.isNaN(n)) return "0.00";
    return n.toFixed(2);
}

function BrandMark({ size = "md" }) {
    const fontSize = size === "lg" ? 26 : size === "sm" ? 18 : 22;
    return (
        <span className="flex items-center gap-2.5">
            <span
                aria-hidden
                className="relative inline-flex items-center justify-center"
                style={{
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    background: "var(--preset-accent)",
                    color: "var(--preset-accent-ink)",
                    fontFamily: "var(--preset-font-display)",
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                    boxShadow: "0 4px 14px -6px var(--preset-accent-glow)",
                }}
            >
                {BRAND.charAt(0).toUpperCase()}
            </span>
            <span
                className="preset2-serif truncate"
                style={{
                    fontSize,
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                    color: "var(--preset-text-primary)",
                }}
            >
                {BRAND}
            </span>
        </span>
    );
}

function Sidebar({ items, onNavigate, unreadCount }) {
    const location = useLocation();
    return (
        <aside
            className="preset2-sidebar h-full flex flex-col"
            style={{
                width: 232,
                background: "var(--preset-surface)",
                borderRight: "1px solid var(--preset-hairline)",
            }}
            data-testid="nocturne-sidebar"
        >
            <Link
                to="/home"
                className="px-6 py-6 preset2-hairline-bottom flex items-center"
                style={{ color: "var(--preset-text-primary)" }}
                data-testid="nocturne-brand"
            >
                <BrandMark />
            </Link>

            <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
                {items.map((it) => {
                    const Icon = it.icon;
                    const isActive = location.pathname.startsWith(it.to);
                    const showDot = it.to === "/inbox" && unreadCount > 0;
                    return (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            onClick={onNavigate}
                            data-testid={`nocturne-nav-${it.label.toLowerCase()}`}
                            data-active={isActive ? "true" : "false"}
                            className="group relative flex items-center gap-3 px-3 py-2.5 text-sm tracking-[0.02em] n-nav-item"
                            style={{
                                color: isActive
                                    ? "var(--preset-accent)"
                                    : "var(--preset-text-secondary)",
                                background: isActive
                                    ? "var(--preset-accent-soft, rgba(212,175,55,0.10))"
                                    : "transparent",
                                border: isActive
                                    ? "1px solid var(--preset-accent-glow, rgba(212,175,55,0.25))"
                                    : "1px solid var(--preset-hairline)",
                                borderRadius: "var(--preset-radius-md, 8px)",
                                transition:
                                    "color 180ms var(--n-ease), background 180ms var(--n-ease), border-color 180ms var(--n-ease)",
                            }}
                        >
                            {isActive && (
                                <motion.span
                                    layoutId="nocturne-nav-ind"
                                    className="absolute left-0 top-1 bottom-1"
                                    style={{
                                        width: 3,
                                        background: "var(--preset-accent)",
                                        borderRadius: "0 2px 2px 0",
                                    }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 420,
                                        damping: 36,
                                    }}
                                />
                            )}
                            <span className="relative inline-flex">
                                <Icon
                                    className="w-4 h-4"
                                    style={{ color: "inherit" }}
                                />
                                {showDot && <span className="n-notif-dot" />}
                            </span>
                            <span className="relative">{it.label}</span>
                            {isActive && (
                                <ChevronRight className="w-3 h-3 ml-auto relative" />
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            <div className="px-5 py-4 preset2-hairline-top">
                <p className="preset2-eyebrow mb-2" style={{ fontSize: 9.5 }}>
                    fraud-monitored 24/7
                </p>
                <Link to="/messages" className="preset2-link text-xs">
                    Need help? Chat with support →
                </Link>
            </div>
        </aside>
    );
}

function GuestTopRight() {
    return (
        <div
            className="flex items-center gap-2"
            data-testid="nocturne-guest-cta"
        >
            <Link
                to="/auth/login"
                className="preset2-btn-ghost text-xs"
                data-testid="nocturne-top-signin"
            >
                Sign in
            </Link>
            <Link
                to="/auth/signup"
                className="preset2-btn-primary text-xs"
                data-testid="nocturne-top-signup"
            >
                Create account
            </Link>
        </div>
    );
}

function BalanceChip({ wallet }) {
    const [open, setOpen] = useState(false);
    const total = (wallet?.balance || 0) + (wallet?.playCredits || 0);
    return (
        <div className="relative" data-testid="nocturne-balance-chip">
            <button
                onClick={() => setOpen((v) => !v)}
                className="preset2-hairline px-3 py-1.5 flex items-center gap-2 text-xs"
                style={{
                    background: "var(--preset-surface)",
                    borderRadius: "var(--preset-radius-md)",
                    transition: "border-color 160ms var(--n-ease)",
                }}
            >
                <span className="preset2-eyebrow" style={{ fontSize: 10 }}>
                    balance
                </span>
                <span
                    className="preset2-mono-tabular"
                    style={{ color: "var(--preset-text-primary)" }}
                >
                    ${formatAmount(total)}
                </span>
            </button>
            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-60 preset2-card n-popover z-30"
                    style={{ background: "var(--preset-surface)" }}
                    data-testid="nocturne-balance-pop"
                >
                    <div className="p-4 preset2-hairline-bottom">
                        <p className="preset2-eyebrow">total</p>
                        <p
                            className="preset2-mono-tabular mt-1"
                            style={{
                                fontSize: 26,
                                color: "var(--preset-text-primary)",
                            }}
                        >
                            <span
                                style={{
                                    color: "var(--preset-text-muted)",
                                    fontSize: "0.65em",
                                    marginRight: 4,
                                }}
                            >
                                $
                            </span>
                            {formatAmount(total)}
                        </p>
                        <p
                            className="preset2-mono-tabular mt-2 text-[11px]"
                            style={{ color: "var(--preset-text-muted)" }}
                        >
                            cash ${formatAmount(wallet?.balance || 0)} · credits
                            ${formatAmount(wallet?.playCredits || 0)}
                        </p>
                    </div>
                    <div className="p-3 grid grid-cols-2 gap-2">
                        <Link
                            to="/wallet"
                            className="preset2-btn-primary text-xs text-center"
                            onClick={() => setOpen(false)}
                        >
                            Deposit
                        </Link>
                        <Link
                            to="/wallet?tab=withdraw"
                            className="preset2-btn-ghost text-xs text-center"
                            onClick={() => setOpen(false)}
                        >
                            Redeem
                        </Link>
                    </div>
                    <div className="p-3 preset2-hairline-top">
                        <Link
                            to="/transactions"
                            className="text-xs preset2-link"
                            onClick={() => setOpen(false)}
                        >
                            All transactions →
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}

function NotificationBell({ unreadCount }) {
    return (
        <Link
            to="/inbox"
            className="relative inline-flex items-center justify-center w-9 h-9 preset2-hairline"
            style={{
                background: "var(--preset-surface)",
                borderRadius: "var(--preset-radius-md)",
                color: "var(--preset-text-primary)",
                transition:
                    "color 160ms var(--n-ease), border-color 160ms var(--n-ease), background-color 160ms var(--n-ease)",
            }}
            data-testid="nocturne-bell"
            aria-label="Notifications"
            title="Notifications"
            onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--preset-accent)";
                e.currentTarget.style.borderColor = "var(--preset-accent)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--preset-text-primary)";
                e.currentTarget.style.borderColor = "";
            }}
        >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && <span className="n-notif-dot" />}
        </Link>
    );
}

// Twitter-style verified blue tick — shows next to a verified user's name.
function BlueTick({ size = 14 }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            data-testid="nocturne-blue-tick"
            style={{
                flexShrink: 0,
                display: "inline-block",
                verticalAlign: "-2px",
            }}
            aria-label="Verified"
        >
            <path
                d="M20.396 11l-2.018-2.318.281-3.062-3.005-.682L13.79 2.282 11 3.5 8.21 2.282 6.346 4.938l-3.005.682.281 3.062L1.604 11l2.018 2.318-.281 3.062 3.005.682 1.864 2.656L11 18.5l2.79 1.218 1.864-2.656 3.005-.682-.281-3.062L20.396 11zM9.297 14.825L6 11.527l1.067-1.067 2.23 2.23 5.636-5.636L16 8.122l-6.703 6.703z"
                fill="#1d9bf0"
            />
        </svg>
    );
}

function DesktopTopBar({ isLoggedIn, wallet, user, unreadCount }) {
    return (
        <div
            className="hidden lg:flex items-center justify-between gap-4 py-3 preset2-hairline-bottom"
            style={{
                background: "var(--preset-bg)",
                position: "sticky",
                top: 0,
                zIndex: 20,
                paddingLeft: "calc(232px + 2rem)",
                paddingRight: "2rem",
            }}
            data-testid="nocturne-topbar"
        >
            {/* Quick action chips */}
            {isLoggedIn ? (
                <div
                    className="flex items-center gap-2"
                    data-testid="nocturne-quick-actions"
                >
                    <Link
                        to="/wallet"
                        className="n-action"
                        data-variant="primary"
                        data-testid="nocturne-quick-deposit"
                    >
                        <ArrowDownLeft className="w-4 h-4" />
                        Deposit
                    </Link>
                    <Link
                        to="/games"
                        className="n-action"
                        data-testid="nocturne-quick-games"
                    >
                        <Gamepad2 className="w-4 h-4" />
                        Games
                    </Link>
                    <Link
                        to="/achievements"
                        className="n-action"
                        data-testid="nocturne-quick-rewards"
                    >
                        <Trophy className="w-4 h-4" />
                        Rewards
                    </Link>
                </div>
            ) : (
                <div />
            )}

            {/* Right cluster */}
            {isLoggedIn ? (
                <div className="flex items-center gap-3">
                    <span
                        className="text-xs truncate max-w-[200px] inline-flex items-center gap-1"
                        style={{ color: "var(--preset-text-primary)" }}
                        data-testid="nocturne-username"
                    >
                        {user?.username || user?.email || "member"}
                        {user?.verified_badge && (
                            <VerifiedBadge size="sm" />
                        )}
                    </span>
                    <BalanceChip wallet={wallet} />
                    <NotificationBell unreadCount={unreadCount} />
                </div>
            ) : (
                <GuestTopRight />
            )}
        </div>
    );
}

function MobileTopStrip({ isLoggedIn, wallet, onOpenDrawer, unreadCount }) {
    const total = isLoggedIn
        ? (wallet?.balance || 0) + (wallet?.playCredits || 0)
        : 0;
    return (
        <header
            className="lg:hidden preset2-hairline-bottom sticky top-0 z-40"
            style={{ background: "var(--preset-bg)" }}
            data-testid="nocturne-mobile-strip"
        >
            <div className="flex items-center justify-between px-4 py-3 gap-3">
                <button
                    onClick={onOpenDrawer}
                    className="p-1.5 -ml-1.5"
                    aria-label="Open menu"
                    data-testid="nocturne-mobile-menu"
                    style={{ color: "var(--preset-text-primary)" }}
                >
                    <MenuIcon className="w-5 h-5" />
                </button>
                <Link to="/home" className="flex-1 min-w-0 flex items-center">
                    <BrandMark size="sm" />
                </Link>
                {isLoggedIn ? (
                    <div className="flex items-center gap-2.5">
                        <Link
                            to="/wallet"
                            className="preset2-mono-tabular text-sm"
                            data-testid="nocturne-mobile-balance"
                        >
                            <span style={{ color: "var(--preset-text-muted)" }}>
                                $
                            </span>
                            {formatAmount(total)}
                        </Link>
                        <Link
                            to="/inbox"
                            className="relative inline-flex items-center justify-center w-8 h-8"
                            style={{ color: "var(--preset-text-secondary)" }}
                            data-testid="nocturne-mobile-bell"
                            aria-label="Inbox"
                        >
                            <Bell className="w-4 h-4" />
                            {unreadCount > 0 && (
                                <span className="n-notif-dot" />
                            )}
                        </Link>
                    </div>
                ) : (
                    <Link
                        to="/auth/login"
                        className="preset2-btn-primary text-xs px-3 py-1.5"
                        data-testid="nocturne-mobile-signin"
                    >
                        Sign in
                    </Link>
                )}
            </div>
        </header>
    );
}


function isVendorTenantHost() {
    if (typeof window === "undefined") return false;
    const host = String(window.location.hostname || "").toLowerCase();
    return (
        host &&
        host !== "multiple.games" &&
        host !== "www.multiple.games" &&
        host !== "zone.multiple.games" &&
        host !== "localhost" &&
        !host.startsWith("127.") &&
        !host.startsWith("10.") &&
        !host.startsWith("192.168.") &&
        !host.endsWith(".local")
    );
}

function MobileBottomNav({ isLoggedIn }) {
    const location = useLocation();
    if (!isLoggedIn) return null;

    const isActive = (to) => location.pathname.startsWith(to);

    return (
        <div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-40 n-dock grid grid-cols-5"
            data-testid="nocturne-bottom-bar"
        >
            {MOBILE_NAV_LEFT.map((it) => {
                const Icon = it.icon;
                return (
                    <Link
                        key={it.to}
                        to={it.to}
                        className="n-dock-item"
                        data-active={isActive(it.to) ? "true" : "false"}
                        data-testid={`nocturne-bottom-${it.label.toLowerCase()}`}
                    >
                        <Icon className="w-4 h-4" />
                        <span className="n-dock-label">{it.label}</span>
                    </Link>
                );
            })}
            <Link
                to="/wallet"
                className="n-dock-item-cta n-dock-item flex flex-col items-center"
                data-active={isActive("/wallet") ? "true" : "false"}
                data-testid="nocturne-bottom-deposit"
            >
                <ArrowDownLeft className="w-4 h-4" />
                <span className="n-dock-label">Deposit</span>
            </Link>
            {MOBILE_NAV_RIGHT.map((it) => {
                const Icon = it.icon;
                return (
                    <Link
                        key={it.to}
                        to={it.to}
                        className="n-dock-item"
                        data-active={isActive(it.to) ? "true" : "false"}
                        data-testid={`nocturne-bottom-${it.label.toLowerCase()}`}
                    >
                        <Icon className="w-4 h-4" />
                        <span className="n-dock-label">{it.label}</span>
                    </Link>
                );
            })}
        </div>
    );
}

function MobileDrawer({
    open,
    onClose,
    items,
    isLoggedIn,
    unreadCount,
    user,
    wallet,
}) {
    if (!open) return null;
    const balance = typeof wallet?.balance === "number" ? wallet.balance : 0;
    return (
        <div
            className="fixed inset-0 z-50 lg:hidden"
            data-testid="nocturne-mobile-drawer"
        >
            <div
                className="absolute inset-0"
                style={{ background: "rgba(8,9,12,0.82)" }}
                onClick={onClose}
            />
            <aside
                className="absolute top-0 left-0 h-full w-[300px] preset2-sidebar flex flex-col"
                style={{
                    background: "var(--preset-bg)",
                    animation: "preset2FadeUp 240ms var(--n-ease) both",
                }}
            >
                <div className="px-5 py-4 flex items-center justify-between preset2-hairline-bottom">
                    <BrandMark />
                    <button
                        onClick={onClose}
                        aria-label="Close menu"
                        className="p-1.5 rounded-full"
                        style={{
                            color: "var(--preset-text-secondary)",
                            background: "rgba(255,255,255,0.04)",
                        }}
                    >
                        <CloseIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* User identity + balance chip */}
                {isLoggedIn && user ? (
                    <div
                        className="mx-4 mt-4 p-4 rounded-xl"
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.02) 100%)",
                            border: "1px solid rgba(212,175,55,0.32)",
                        }}
                        data-testid="nocturne-drawer-user-card"
                    >
                        <div className="flex items-center gap-3">
                            <div
                                className="shrink-0 preset2-serif flex items-center justify-center"
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    background: "var(--preset-accent)",
                                    color: "var(--preset-bg)",
                                    fontSize: 16,
                                    fontWeight: 700,
                                }}
                            >
                                {(user.username || user.email || "?")
                                    .charAt(0)
                                    .toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p
                                    className="text-sm truncate inline-flex items-center gap-1"
                                    style={{
                                        color: "var(--preset-text-primary)",
                                        fontWeight: 600,
                                    }}
                                >
                                    {user.username || user.email || "Player"}
                                    {user.verified_badge && (
                                        <VerifiedBadge size="sm" />
                                    )}
                                </p>
                                <p
                                    className="text-[10px] preset2-eyebrow mt-0.5"
                                    style={{
                                        color: "var(--preset-text-muted)",
                                    }}
                                >
                                    {user.role === "admin"
                                        ? "admin · full access"
                                        : "member"}
                                </p>
                            </div>
                        </div>
                        <div
                            className="mt-3 pt-3 flex items-baseline justify-between"
                            style={{
                                borderTop:
                                    "1px solid var(--preset-surface-border)",
                            }}
                        >
                            <span
                                className="preset2-eyebrow"
                                style={{
                                    color: "var(--preset-text-muted)",
                                    fontSize: 9,
                                }}
                            >
                                balance
                            </span>
                            <span
                                className="preset2-mono-tabular"
                                style={{
                                    color: "var(--preset-accent)",
                                    fontSize: 18,
                                }}
                            >
                                ${formatAmount(balance)}
                            </span>
                        </div>
                    </div>
                ) : null}

                <nav
                    className="flex-1 px-3 py-4 overflow-y-auto"
                    data-testid="nocturne-drawer-nav"
                >
                    <p
                        className="preset2-eyebrow px-3 mb-2"
                        style={{
                            color: "var(--preset-text-muted)",
                            fontSize: 9,
                        }}
                    >
                        navigate
                    </p>
                    <div className="space-y-1">
                        {items.map((it) => {
                            const Icon = it.icon;
                            const showDot =
                                it.to === "/inbox" && unreadCount > 0;
                            return (
                                <NavLink
                                    key={`d-${it.to}`}
                                    to={it.to}
                                    onClick={onClose}
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors"
                                    style={({ isActive }) => ({
                                        color: isActive
                                            ? "var(--preset-bg)"
                                            : "var(--preset-text-secondary)",
                                        background: isActive
                                            ? "var(--preset-accent)"
                                            : "transparent",
                                        fontWeight: isActive ? 600 : 500,
                                    })}
                                >
                                    <span className="relative inline-flex">
                                        <Icon className="w-4 h-4" />
                                        {showDot && (
                                            <span className="n-notif-dot" />
                                        )}
                                    </span>
                                    <span className="flex-1">{it.label}</span>
                                    <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                                </NavLink>
                            );
                        })}
                    </div>

                    {/* Quick actions */}
                    {isLoggedIn && (
                        <div className="mt-6">
                            <p
                                className="preset2-eyebrow px-3 mb-2"
                                style={{
                                    color: "var(--preset-text-muted)",
                                    fontSize: 9,
                                }}
                            >
                                quick actions
                            </p>
                            <div className="grid grid-cols-2 gap-2 px-3">
                                <Link
                                    to="/wallet?intent=deposit"
                                    onClick={onClose}
                                    className="text-xs py-2.5 text-center rounded-lg"
                                    style={{
                                        background: "var(--preset-accent)",
                                        color: "var(--preset-bg)",
                                        fontWeight: 600,
                                        letterSpacing: "0.04em",
                                    }}
                                    data-testid="nocturne-drawer-deposit"
                                >
                                    <ArrowDownLeft className="w-3.5 h-3.5 inline mr-1" />
                                    Deposit
                                </Link>
                                <Link
                                    to="/wallet?intent=withdraw"
                                    onClick={onClose}
                                    className="text-xs py-2.5 text-center rounded-lg"
                                    style={{
                                        background: "rgba(255,255,255,0.04)",
                                        border: "1px solid var(--preset-surface-border)",
                                        color: "var(--preset-text-primary)",
                                        fontWeight: 500,
                                    }}
                                    data-testid="nocturne-drawer-withdraw"
                                >
                                    <ArrowUpRight className="w-3.5 h-3.5 inline mr-1" />
                                    Withdraw
                                </Link>
                            </div>
                        </div>
                    )}
                </nav>

                {!isLoggedIn ? (
                    <div className="p-4 space-y-2 preset2-hairline-top">
                        <Link
                            to="/auth/login"
                            onClick={onClose}
                            className="preset2-btn-primary text-xs w-full block text-center"
                        >
                            Sign in
                        </Link>
                        <Link
                            to="/auth/signup"
                            onClick={onClose}
                            className="preset2-btn-ghost text-xs w-full block text-center"
                        >
                            Create account
                        </Link>
                    </div>
                ) : (
                    <div
                        className="p-4 preset2-hairline-top text-[10px]"
                        style={{ color: "var(--preset-text-muted)" }}
                    >
                        <p className="preset2-eyebrow" style={{ fontSize: 9 }}>
                            fraud-monitored 24/7
                        </p>
                        <p className="mt-1">
                            Need help?{" "}
                            <Link
                                to="/messages"
                                onClick={onClose}
                                className="preset2-link"
                            >
                                Chat with support →
                            </Link>
                        </p>
                    </div>
                )}
            </aside>
        </div>
    );
}

export default function NocturneShell({ children }) {
    if (isVendorTenantHost()) {
        return <>{children}</>;
    }
    return <NocturneShellContent>{children}</NocturneShellContent>;
}

// Tenant routes intentionally bypass the main app provider. Mount the hook-using
// shell only on the main app, rather than calling hooks after a conditional return.
function NocturneShellContent({ children }) {
    const { user, wallet, isLoggedIn, unreadCount } = useApp();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const items = useMemo(
        () =>
            NAV_ITEMS.filter(
                (i) => !i.capability || hasCapability(i.capability),
            ),
        [],
    );

    return (
        <div
            className="min-h-screen flex flex-col lg:flex-row"
            style={{ background: "var(--preset-bg)" }}
            data-preset-shell="nocturne"
        >
            <div
                className="hidden lg:block lg:fixed lg:top-0 lg:left-0 lg:h-screen lg:flex-shrink-0"
                style={{ zIndex: 30, width: 232 }}
            >
                <Sidebar items={items} unreadCount={unreadCount || 0} />
            </div>

            <MobileTopStrip
                isLoggedIn={isLoggedIn}
                wallet={wallet}
                onOpenDrawer={() => setDrawerOpen(true)}
                unreadCount={unreadCount || 0}
            />
            <MobileDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                items={items}
                isLoggedIn={isLoggedIn}
                unreadCount={unreadCount || 0}
                user={user}
                wallet={wallet}
            />

            <div className="flex-1 min-w-0 flex flex-col">
                <DesktopTopBar
                    isLoggedIn={isLoggedIn}
                    wallet={wallet}
                    user={user}
                    unreadCount={unreadCount || 0}
                />
                <main
                    className="flex-1 min-w-0 preset2-enter pb-24 lg:pb-0 lg:pl-[232px]"
                    data-testid="nocturne-main"
                >
                    {children}
                </main>
            </div>

            <MobileBottomNav isLoggedIn={isLoggedIn} />
        </div>
    );
}
